import { useMemo } from "react";
import type { FilterState } from "@/types/filters";
import type { SchedulingRow } from "@/types/scheduling";
import {
  applyBaseFilter,
  filterRowsByDateRange,
  filterRowsByScope,
  sumSelectedKindMinutes,
} from "@/lib/metrics";
import {
  getBucketKey,
  getPeriodFamily,
  getWeekKey,
  isBucketIncluded,
  type PeriodFamily,
} from "@/lib/periods";
import {
  deriveTrendPoint,
  type TrendBucketTotals,
  type TrendPoint,
  type TrendSeries,
} from "@/lib/trendMetrics";

export type { TrendPoint, TrendSeries } from "@/lib/trendMetrics";

// A DVM-day is one doctor on one date, deduped so a doctor split across two
// sites the same day (two scheduling_fact rows) isn't counted twice — matches
// the convention used in useRevenueTrendData's dvmDayKey.
function dvmDayKey(r: SchedulingRow): string {
  return `${r.resource_id}|${r.schedule_date}`;
}

export interface TrendData {
  util: TrendSeries;
  cap: TrendSeries;
}

function buildPoints(
  rows: SchedulingRow[],
  startDate: string,
  endDate: string,
  granularity: "day" | "week" | "month",
  appointmentTypes: string[],
  family: PeriodFamily
): TrendPoint[] {
  const range = { startDate, endDate };
  const typeFilterActive = appointmentTypes.length > 0;

  const bucketMap = new Map<string, SchedulingRow[]>();
  for (const row of filterRowsByDateRange(rows, startDate, endDate)) {
    const key = getBucketKey(row.schedule_date, granularity, family);
    // Every overlapping bucket is plotted now, including a calendar period's
    // part-weeks. They used to be dropped because a two-day stub read ~3.5x low
    // on per-week metrics; deriveTrendPoint scales the DVM-week denominator by the
    // fraction of the week inside the range, so the rate is right and the chart
    // covers the same days as the KPI cards.
    if (!isBucketIncluded(key, granularity, family, range)) continue;
    if (!bucketMap.has(key)) bucketMap.set(key, []);
    bucketMap.get(key)!.push(row);
  }

  // Reduce rows to raw totals here, and let deriveTrendPoint own every ratio.
  // This hook used to duplicate that arithmetic, which meant a subtle fix -- the
  // partial-week denominator clip, for instance -- had to be written twice and
  // could drift. Section 6.2 of the aggregation plan is a case where two copies
  // of the same formula did exactly that.
  return Array.from(bucketMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, bRows]) => {
      const totals: TrendBucketTotals = {
        bucketKey: key,
        dvmDays: new Set(bRows.map(dvmDayKey)).size,
        dvmWeeks: new Set(bRows.map((r) => `${r.resource_id}|${getWeekKey(r.schedule_date)}`)).size,
        distinctDates: new Set(bRows.map((r) => r.schedule_date)).size,
        totalScheduledMin: bRows.reduce((t, r) => t + Number(r.scheduled_minutes), 0),
        totalWorkedMin: bRows.reduce((t, r) => t + Number(r.total_worked_minutes), 0),
        totalBlockoffMin: bRows.reduce((t, r) => t + Number(r.blockoff_minutes), 0),
        allSeenMin: bRows.reduce((t, r) => t + Number(r.completed_appointment_minutes), 0),
        totalAvailMin: bRows.reduce((t, r) => t + Number(r.available_minutes), 0),
        selectedKindMin: typeFilterActive ? sumSelectedKindMinutes(bRows, appointmentTypes) : 0,
        totalMedRev: bRows.reduce((t, r) => t + (Number(r.medical_net_sales_amount) || 0), 0),
        totalCompleted: bRows.reduce((t, r) => t + (Number(r.completed_appointments) || 0), 0),
      };
      return deriveTrendPoint(totals, granularity, family, range, typeFilterActive);
    });
}

export function useTrendData(rows: SchedulingRow[], filters: FilterState): TrendData {
  // Destructured outside the memo so the deps can name the fields read here.
  // capacityView and capacityMetric are display-only; depending on the whole
  // `filters` object made a card toggle rebuild every trend bucket.
  const {
    customStart,
    customEnd,
    appointmentTypes,
    period,
    scope,
    excludeZeroApptDays,
    utilGranularity,
    capGranularity,
  } = filters;
  return useMemo(() => {
    const base = applyBaseFilter(rows, excludeZeroApptDays);
    const scoped = filterRowsByScope(base, scope);
    const family = getPeriodFamily(period);

    const series = (granularity: "day" | "week" | "month"): TrendSeries => ({
      current: buildPoints(scoped, customStart, customEnd, granularity, appointmentTypes, family),
    });

    return { util: series(utilGranularity), cap: series(capGranularity) };
    // Specific fields, not the whole object: capacityView and capacityMetric are
    // display-only, and depending on `filters` made a card toggle rebuild every
    // trend bucket. Both granularities are listed because each chart reads its own.
  }, [
    rows,
    excludeZeroApptDays,
    customStart,
    customEnd,
    period,
    scope,
    appointmentTypes,
    utilGranularity,
    capGranularity,
  ]);
}
